// src/components/Navbar.js
import React, { useState } from 'react';
import {
    AppBar,
    Toolbar,
    IconButton,
    Button,
    Box,
    Menu,
    MenuItem,
    Typography,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    useMediaQuery,
} from '@mui/material';
import {
    Home,
    ArtTrack,
    AdminPanelSettings,
    Info as InfoIcon,
    MailRounded as MailIcon,
    Menu as MenuIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';

// Modern el yazısı (handwriting) fontu için @fontsource paketi
import '@fontsource/great-vibes';

const Navbar = () => {
    const { i18n, t } = useTranslation();
    const currentLang = i18n.language || 'en';
    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
    };
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [langAnchorEl, setLangAnchorEl] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const openLangMenu = (event) => setLangAnchorEl(event.currentTarget);
    const closeLangMenu = () => setLangAnchorEl(null);

    const languages = {
        en: { label: 'English', flag: 'https://flagcdn.com/20x15/us.png' },
        tr: { label: 'Türkçe', flag: 'https://flagcdn.com/20x15/tr.png' },
    };

    const menuItems = [
        { label: t('home'), to: '/', icon: <Home /> },
        { label: t('biografi'), to: '/biografi', icon: <InfoIcon /> },
        { label: t('contact'), to: '/contact', icon: <MailIcon /> },
        { label: t('works'), to: '/works', icon: <ArtTrack /> },
        { label: t('admin'), to: '/admin', icon: <AdminPanelSettings /> },
    ];

    // Menü için ortak render fonksiyonu
    const renderMenuItems = (onClick) =>
        menuItems.map((item) => (
            <ListItem
                button
                key={item.label}
                component={Link}
                to={item.to}
                onClick={onClick}
                sx={{ color: 'inherit' }}
            >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>{item.icon}</ListItemIcon>
                <ListItemText
                    primary={
                        <Typography variant="button" sx={{ fontWeight: 400 }}>
                            {item.label}
                        </Typography>
                    }
                />
            </ListItem>
        ));

    return (
        <AppBar
            position="fixed"
            sx={{
                bgcolor: '#232323',
                boxShadow: 'none',
            }}
        >
            <Toolbar
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    minHeight: 64,
                }}
            >
                {/* Sol: Seyhan Erdem (Şık, modern el yazısı fontu) */}
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 180 }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontFamily: 'Great Vibes, cursive',
                            fontWeight: 'normal',
                            letterSpacing: 1,
                            color: '#fff',
                        }}
                    >
                        Seyhan{' '}
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Erdem
                        </Box>
                    </Typography>
                </Box>

                {/* Sağ: Menü */}
                {!isMobile ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {menuItems.map((item) => (
                            <Button
                                key={item.to}
                                component={Link}
                                to={item.to}
                                color="inherit"
                                startIcon={item.icon}
                                sx={{ textTransform: 'none' }}
                            >
                                <Typography variant="button">{item.label}</Typography>
                            </Button>
                        ))}
                        <IconButton color="inherit" onClick={openLangMenu} sx={{ ml: 1 }}>
                            <Box
                                component="img"
                                src={languages[currentLang].flag}
                                alt={currentLang.toUpperCase()}
                                sx={{ width: 24, height: 18 }}
                            />
                        </IconButton>
                        <Menu
                            anchorEl={langAnchorEl}
                            open={Boolean(langAnchorEl)}
                            onClose={closeLangMenu}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            {Object.entries(languages).map(([code, { label, flag }]) => (
                                <MenuItem
                                    key={code}
                                    selected={code === currentLang}
                                    onClick={() => {
                                        changeLanguage(code);
                                        closeLangMenu();
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={flag}
                                        alt={code.toUpperCase()}
                                        sx={{ width: 20, height: 15, mr: 1 }}
                                    />
                                    <Typography variant="body2">{label}</Typography>
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                ) : (
                    <>
                        <IconButton
                            color="inherit"
                            onClick={() => setDrawerOpen(true)}
                            sx={{ ml: 2 }}
                            aria-label="menu"
                        >
                            <MenuIcon fontSize="large" />
                        </IconButton>
                        <Drawer
                            anchor="right"
                            open={drawerOpen}
                            onClose={() => setDrawerOpen(false)}
                            PaperProps={{
                                sx: { bgcolor: '#232323', color: '#fff', width: 250 },
                            }}
                        >
                            <Box sx={{ my: 2 }}>
                                <Typography
                                    variant="h6"
                                    sx={{ textAlign: 'center', fontWeight: 'bold' }}
                                >
                                    Menü
                                </Typography>
                            </Box>
                            <Divider sx={{ bgcolor: '#444' }} />
                            <List>
                                {renderMenuItems(() => setDrawerOpen(false))}
                            </List>
                            <Divider sx={{ bgcolor: '#444', my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                                <IconButton color="inherit" onClick={openLangMenu}>
                                    <Box
                                        component="img"
                                        src={languages[currentLang].flag}
                                        alt={currentLang.toUpperCase()}
                                        sx={{ width: 24, height: 18 }}
                                    />
                                </IconButton>
                                <Menu
                                    anchorEl={langAnchorEl}
                                    open={Boolean(langAnchorEl)}
                                    onClose={closeLangMenu}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                >
                                    {Object.entries(languages).map(([code, { label, flag }]) => (
                                        <MenuItem
                                            key={code}
                                            selected={code === currentLang}
                                            onClick={() => {
                                                changeLanguage(code);
                                                closeLangMenu();
                                                setDrawerOpen(false);
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={flag}
                                                alt={code.toUpperCase()}
                                                sx={{ width: 20, height: 15, mr: 1 }}
                                            />
                                            <Typography variant="body2">{label}</Typography>
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Box>
                        </Drawer>
                    </>
                )}
            </Toolbar>
        </AppBar>
    );
};

export default Navbar;
